import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { YouTubeEmbed } from "../components/YouTubeEmbed";
import type { YouTubePlayerHandle } from "../components/YouTubeEmbed";
import { Highlight, ScoreDots } from "../components/atoms";
import { useCollections } from "../data/collections";
import { useVideo } from "../data/hooks";
import { youtubeTimeUrl } from "../lib/citation";
import { formatDuration, formatTimestamp } from "../lib/transcript";
import type { CollectedQuote, TranscriptSegment, VideoDoc } from "../lib/types";

type LangMode = "both" | "en" | "orig";

const LANG_LABELS: readonly { value: LangMode; label: string }[] = [
  { value: "both", label: "Both" },
  { value: "en", label: "English" },
  { value: "orig", label: "Original" },
];

function Sidebar({
  video,
  onSeek,
}: {
  video: VideoDoc;
  onSeek: (t: number) => void;
}) {
  return (
    <aside className="space-y-6 text-sm">
      <div className="space-y-1">
        <p className="eyebrow">Speaker</p>
        <Link
          to={`/speakers/${video.speakerKey}`}
          className="font-serif text-lg text-warm-brown hover:text-terra"
        >
          {video.speaker}
        </Link>
      </div>

      {video.setting !== null ? (
        <div>
          <p className="eyebrow mb-1">Setting</p>
          <p className="text-warm-brown-light">{video.setting}</p>
        </div>
      ) : null}

      {video.scores !== null ? (
        <div>
          <p className="eyebrow mb-2">Theme relevance</p>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-warm-gray">Harmony of faiths</span>
              <ScoreDots
                score={video.scores.harmony}
                label="Harmony of faiths"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-warm-gray">Inner wellbeing</span>
              <ScoreDots
                score={video.scores.wellbeing}
                label="Inner wellbeing"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-warm-gray">Universal peace</span>
              <ScoreDots score={video.scores.peace} label="Universal peace" />
            </div>
          </div>
        </div>
      ) : null}

      {video.teachings.length > 0 ? (
        <div>
          <p className="eyebrow mb-2">Core teachings</p>
          <ul className="space-y-2">
            {video.teachings.map((teaching) => (
              <li key={teaching.t} className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onSeek(teaching.t)}
                  className="shrink-0 tabular-nums text-terra hover:underline"
                >
                  {formatTimestamp(teaching.t)}
                </button>
                <span className="text-warm-brown-light">{teaching.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {video.gems.length > 0 ? (
        <div>
          <p className="eyebrow mb-2">Highlights</p>
          <p className="mb-2 text-xs text-warm-gray">
            Paraphrased by the digest pass, not verbatim. Collect from the
            transcript for an exact quote.
          </p>
          <ul className="space-y-2">
            {video.gems.map((gem) => (
              <li
                key={gem}
                className="border-l-2 border-gold/40 pl-3 font-serif italic text-warm-brown-light"
              >
                {gem}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </aside>
  );
}

/** Scroll a transcript line to just below the sticky player, not behind it. */
function scrollToLine(index: number) {
  const element = document.getElementById(`line-${index}`);
  if (element === null) {
    return;
  }
  const sticky = document.getElementById("video-sticky");
  const offset = (sticky?.getBoundingClientRect().height ?? 0) + 80;
  const top = element.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

export function VideoScreen() {
  const params = useParams();
  const id = params.id ?? "";
  const [searchParams] = useSearchParams();
  const initialT = Number(searchParams.get("t")) || 0;

  const { video, loading, missing } = useVideo(id);
  const player = useRef<YouTubePlayerHandle>(null);
  const [lang, setLang] = useState<LangMode>("both");
  const [find, setFind] = useState("");
  const [matchPos, setMatchPos] = useState(0);
  const { add, has } = useCollections();

  const findTerm = find.trim().toLowerCase();
  const matches = useMemo(() => {
    if (findTerm.length < 2 || video === null) {
      return [];
    }
    const indices: number[] = [];
    video.segments.forEach((segment, index) => {
      if (
        segment.en.toLowerCase().includes(findTerm) ||
        segment.orig.toLowerCase().includes(findTerm)
      ) {
        indices.push(index);
      }
    });
    return indices;
  }, [findTerm, video]);

  useEffect(() => {
    if (video === null || initialT <= 0) {
      return;
    }
    const forward = video.segments.findIndex(
      (segment) => segment.t >= initialT,
    );
    const target = forward === -1 ? video.segments.length - 1 : forward;
    // Wait a frame so the player has laid out before measuring its height.
    const raf = window.requestAnimationFrame(() => scrollToLine(target));
    return () => window.cancelAnimationFrame(raf);
  }, [video, initialT]);

  if (loading) {
    return <p className="text-warm-gray">Loading the discourse…</p>;
  }
  if (missing || video === null) {
    return (
      <p className="text-warm-gray">
        That video is not in the library.{" "}
        <Link to="/library" className="text-terra hover:underline">
          Back to the library
        </Link>
        .
      </p>
    );
  }

  const seek = (t: number) => player.current?.seekTo(t);
  const findTerms = findTerm.length >= 2 ? [findTerm] : [];

  const jumpToMatch = (position: number) => {
    if (matches.length === 0) {
      return;
    }
    const wrapped = (position + matches.length) % matches.length;
    setMatchPos(wrapped);
    scrollToLine(matches[wrapped]);
  };

  const quoteOf = (segment: TranscriptSegment): CollectedQuote => ({
    videoId: video.id,
    title: video.title,
    speaker: video.speaker,
    url: video.url,
    t: segment.t,
    en: segment.en,
    orig: segment.orig,
  });

  return (
    <div>
      <header className="mb-6">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-warm-gray">
          <Link to="/library" className="hover:text-terra">
            Library
          </Link>
          <span>/</span>
          <span>{video.series}</span>
          {video.year !== null ? <span>· {video.year}</span> : null}
          <span>· {formatDuration(video.duration)}</span>
        </div>
        <h1 className="font-display text-2xl text-warm-brown sm:text-3xl">
          {video.title}
        </h1>
      </header>

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div
            id="video-sticky"
            className="sticky top-16 z-10 -mx-1 mb-5 bg-cream px-1 pb-2"
          >
            <YouTubeEmbed
              ref={player}
              videoId={video.id}
              title={video.title}
              start={initialT}
            />
            <a
              href={youtubeTimeUrl(video.url, 0)}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-xs text-warm-gray hover:text-terra"
            >
              Watch on YouTube
            </a>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="flex overflow-hidden rounded-full border border-hairline text-sm">
              {LANG_LABELS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setLang(option.value)}
                  className={`px-3 py-1 ${
                    lang === option.value
                      ? "bg-terra text-warm-white"
                      : "text-warm-gray hover:text-warm-brown"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <input
                value={find}
                onChange={(event) => {
                  setFind(event.target.value);
                  setMatchPos(0);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    jumpToMatch(matchPos + (matches.length > 0 ? 1 : 0));
                  }
                }}
                placeholder="Find in transcript"
                className="rounded-md border border-hairline bg-warm-white px-2 py-1 text-warm-brown"
              />
              {findTerm.length >= 2 ? (
                <span className="text-xs text-warm-gray">
                  {matches.length} found
                  {matches.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => jumpToMatch(matchPos + 1)}
                      className="ml-2 text-terra hover:underline"
                    >
                      Next
                    </button>
                  ) : null}
                </span>
              ) : null}
            </div>
          </div>

          <p className="mb-3 text-xs text-warm-gray">
            Tap a line to play the video from that moment. Use{" "}
            <span className="text-terra">Collect</span> to save a line as a
            quote.
          </p>

          <div className="divide-y divide-hairline">
            {video.segments.map((segment, index) => {
              const collected = has(video.id, segment.t);
              return (
                <div
                  key={segment.t}
                  id={`line-${index}`}
                  className="group flex gap-3 py-2.5"
                >
                  <button
                    type="button"
                    onClick={() => seek(segment.t)}
                    className="w-12 shrink-0 pt-0.5 text-left text-xs tabular-nums text-terra hover:underline"
                  >
                    {formatTimestamp(segment.t)}
                  </button>
                  <div className="flex-1">
                    {(lang === "both" || lang === "en") &&
                    segment.en.length > 0 ? (
                      <p className="leading-relaxed text-warm-brown">
                        <Highlight text={segment.en} terms={findTerms} />
                      </p>
                    ) : null}
                    {(lang === "both" || lang === "orig") &&
                    segment.orig.length > 0 ? (
                      <p className="font-devanagari text-warm-brown-light">
                        <Highlight text={segment.orig} terms={findTerms} />
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => add(quoteOf(segment))}
                    disabled={collected}
                    className="h-6 shrink-0 self-start rounded-full border border-hairline px-2 text-xs text-warm-gray opacity-45 transition-opacity hover:text-terra group-hover:opacity-100 disabled:opacity-100"
                    aria-label={collected ? "Collected" : "Collect this line"}
                  >
                    {collected ? "Collected" : "Collect"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <Sidebar video={video} onSeek={seek} />
      </div>
    </div>
  );
}
